import { FormEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui';
import { useCreatePatient, useUpdatePatient } from '@/services';
import { PatientSchedulingFormFields } from './PatientSchedulingFormFields';
import {
	buildPatientBodyFromForm,
	INITIAL_PATIENT_FORM_DATA,
	patientToFormData,
	validatePatientForm,
	type PatientFormData,
} from '../utils/patientSchedulingForm';
import {
	PATIENT_PHONE_INPUT_ID,
	PATIENT_WHATSAPP_SECTION_ID,
} from '@/utils/patientWhatsAppNotifications';
import { isValidWhatsAppPhone } from '@/utils/sessionWhatsAppDisplay';
import { removeMask } from '@/utils/masks';

interface CreatePatientModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: (patient: Patients.Patient) => void;
	/** Quando informado, o modal abre em modo edição. */
	patient?: Patients.Patient | null;
	/** Admin/Support: cria paciente vinculado ao profissional informado. */
	professionalId?: string;
	/** Admin não pode alterar preferência no PATCH — apenas no create. */
	canEditWhatsAppNotifications?: boolean;
	/** Rola até a seção de notificações WhatsApp ao abrir. */
	scrollToWhatsAppSection?: boolean;
}

const CreatePatientModal = ({
	isOpen,
	onClose,
	onSuccess,
	patient = null,
	professionalId,
	canEditWhatsAppNotifications,
	scrollToWhatsAppSection = false,
}: CreatePatientModalProps) => {
	const isEditMode = !!patient;
	const whatsAppEditable = canEditWhatsAppNotifications ?? true;
	const [formData, setFormData] = useState<PatientFormData>(
		INITIAL_PATIENT_FORM_DATA,
	);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const resetAndClose = () => {
		setErrors({});
		setFormData(INITIAL_PATIENT_FORM_DATA);
		onClose();
	};

	const { mutateAsync: createPatient, isPending: isCreating } =
		useCreatePatient((createdPatient) => {
			if (onSuccess) onSuccess(createdPatient);
			resetAndClose();
		});

	const { mutateAsync: updatePatient, isPending: isUpdating } =
		useUpdatePatient((updatedPatient) => {
			if (onSuccess) onSuccess(updatedPatient);
			resetAndClose();
		});

	const isPending = isCreating || isUpdating;

	useEffect(() => {
		if (!isOpen) return;
		setErrors({});
		setFormData(
			patient ? patientToFormData(patient) : INITIAL_PATIENT_FORM_DATA,
		);
	}, [isOpen, patient]);

	useEffect(() => {
		if (!isOpen || !scrollToWhatsAppSection) return;

		const timer = window.setTimeout(() => {
			document
				.getElementById(PATIENT_WHATSAPP_SECTION_ID)
				?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}, 150);

		return () => window.clearTimeout(timer);
	}, [isOpen, scrollToWhatsAppSection, patient?.id]);

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		const nextErrors = validatePatientForm(formData);
		setErrors(nextErrors);
		if (Object.keys(nextErrors).length > 0) {
			const phoneDigits = removeMask(formData.phone);
			if (
				formData.whatsapp_notifications_enabled &&
				!isValidWhatsAppPhone(phoneDigits)
			) {
				toast.error(
					'Para ativar a notificação WhatsApp, informe um telefone válido com 11 dígitos.',
				);
				document.getElementById(PATIENT_PHONE_INPUT_ID)?.focus();
				document
					.getElementById(PATIENT_WHATSAPP_SECTION_ID)
					?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
			return;
		}

		const body = buildPatientBodyFromForm(formData);
		if (isEditMode && patient) {
			await updatePatient({ id: patient.id, body });
			return;
		}
		await createPatient(professionalId ? { body, professionalId } : body);
	};

	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={isPending ? undefined : resetAndClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<form
					onSubmit={handleSubmit}
					noValidate
					className="flex flex-col overflow-hidden"
				>
					<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-8 py-6">
						<h2 className="text-lg font-black text-stone-900">
							{isEditMode ? 'Editar Paciente' : 'Novo Paciente'}
						</h2>
						<button
							type="button"
							onClick={resetAndClose}
							disabled={isPending}
							className="text-stone-400 transition-colors hover:text-stone-600 disabled:opacity-50"
						>
							<X size={20} />
						</button>
					</div>

					<div className="flex-1 overflow-y-auto p-8">
						<PatientSchedulingFormFields
							formData={formData}
							errors={errors}
							onChange={setFormData}
							canEditWhatsAppNotifications={whatsAppEditable}
						/>
					</div>

					<div className="flex shrink-0 items-center justify-end gap-6 border-t border-stone-50 p-8">
						<button
							type="button"
							onClick={resetAndClose}
							disabled={isPending}
							className="text-sm font-bold text-stone-400 hover:text-stone-600 disabled:opacity-50"
						>
							Cancelar
						</button>
						<Button
							type="submit"
							text={
								isPending
									? isEditMode
										? 'Salvando...'
										: 'Criando...'
									: isEditMode
									  ? 'Salvar alterações'
									  : 'Criar Paciente'
							}
							disabled={isPending}
							className="min-w-[160px] rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800"
							startIcon={
								isPending ? (
									<Loader2 className="size-4 animate-spin" />
								) : (
									<Check className="size-4 stroke-[3]" />
								)
							}
						/>
					</div>
				</form>
			</div>
		</div>,
		document.body,
	);
};

export { CreatePatientModal };
